export interface AABB {
  minX: number; maxX: number
  minY: number; maxY: number
  minZ: number; maxZ: number
  /** False for physically solid but visually transparent geometry such as glass. */
  blocksSight?: boolean
}

const _registry: AABB[] = []

/**
 * Placement applied to colliders as they are registered, so a subsystem can
 * author its boxes in LOCAL space and be positioned as a unit. A quarter-turn
 * about Y maps an axis-aligned box to another axis-aligned box, so the whole
 * transform stays exactly representable as an AABB — which is why rotation is
 * restricted to multiples of 90 degrees.
 */
export interface ColliderPlacement {
  /** Quarter turns about Y: 0, 90, 180 or 270 degrees. */
  rotY: 0 | 90 | 180 | 270
  dx: number
  dz: number
}

const IDENTITY_PLACEMENT: ColliderPlacement = { rotY: 0, dx: 0, dz: 0 }
let _placement: ColliderPlacement = IDENTITY_PLACEMENT

/** Set the placement applied to subsequently registered colliders. */
export function setColliderPlacement(placement: ColliderPlacement | null): void {
  _placement = placement ?? IDENTITY_PLACEMENT
}

/** Apply a quarter-turn placement to a local-space box. Pure. */
export function placeBox(box: AABB, p: ColliderPlacement): AABB {
  const corners: Array<[number, number]> = [
    [box.minX, box.minZ], [box.maxX, box.minZ],
    [box.minX, box.maxZ], [box.maxX, box.maxZ],
  ]
  const rad = (p.rotY * Math.PI) / 180
  // Snapped so a quarter turn is exact rather than 6.1e-17 off.
  const cos = Math.round(Math.cos(rad))
  const sin = Math.round(Math.sin(rad))
  const xs: number[] = []
  const zs: number[] = []
  for (const [x, z] of corners) {
    xs.push(x * cos + z * sin + p.dx)
    zs.push(-x * sin + z * cos + p.dz)
  }
  return {
    ...box,
    minX: Math.min(...xs), maxX: Math.max(...xs),
    minY: box.minY, maxY: box.maxY,
    minZ: Math.min(...zs), maxZ: Math.max(...zs),
  }
}

export function registerCollider(box: AABB): void {
  _registry.push(_placement === IDENTITY_PLACEMENT ? box : placeBox(box, _placement))
}

export function getColliders(): readonly AABB[] {
  return _registry
}

export function clearColliders(): void {
  _registry.length = 0
  _placement = IDENTITY_PLACEMENT
}

const SIGHTLINE_ENDPOINT_EPSILON = 1e-4

/**
 * Return whether a finite 3D segment crosses the interior of an AABB between
 * its endpoints. Contact only at the camera or target endpoint is ignored so
 * the floor supporting an agent, or geometry immediately behind it, cannot
 * spuriously hide a spatial affordance.
 */
export function segmentCrossesAabb(
  start: Vec3,
  end: Vec3,
  box: AABB,
  endpointEpsilon = SIGHTLINE_ENDPOINT_EPSILON,
): boolean {
  let tEnter = 0
  let tExit = 1
  const testAxis = (
    origin: number,
    delta: number,
    min: number,
    max: number,
  ): boolean => {
    if (Math.abs(delta) <= EPS) {
      return origin >= min - EPS && origin <= max + EPS
    }
    let near = (min - origin) / delta
    let far = (max - origin) / delta
    if (near > far) [near, far] = [far, near]
    tEnter = Math.max(tEnter, near)
    tExit = Math.min(tExit, far)
    return tEnter <= tExit + EPS
  }

  if (
    !testAxis(start.x, end.x - start.x, box.minX, box.maxX) ||
    !testAxis(start.y, end.y - start.y, box.minY, box.maxY) ||
    !testAxis(start.z, end.z - start.z, box.minZ, box.maxZ)
  ) return false

  return tEnter > endpointEpsilon && tEnter < 1 - endpointEpsilon
}

/** Arena-owned visibility policy; Portal projection never sees world geometry. */
export function hasClearSightline(
  start: Vec3,
  end: Vec3,
  colliders: readonly AABB[],
): boolean {
  return !colliders.some((box) =>
    box.blocksSight !== false && segmentCrossesAabb(start, end, box))
}

/**
 * Resolve a player capsule (foot at pos, radius r, total height h) against static AABBs.
 * The capsule has a circular XZ cross-section; collision uses closest-point-on-AABB math.
 * Resolves on the minimum-penetration axis (Y vs XZ).
 * grounded=true when the player was pushed upward by a surface below.
 */
export function resolveCollisions(
  pos: { x: number; y: number; z: number },
  radius: number,
  height: number,
  colliders: readonly AABB[],
): { x: number; y: number; z: number; grounded: boolean } {
  let { x, y, z } = pos
  let grounded = false

  for (const box of colliders) {
    // Y range overlap (foot at y, head at y + height)
    const overlapY = Math.min(y + height, box.maxY) - Math.max(y, box.minY)
    if (overlapY <= 0) continue

    // XZ capsule (circle) vs AABB rectangle — find closest point on AABB to circle center
    const closestX = Math.max(box.minX, Math.min(x, box.maxX))
    const closestZ = Math.max(box.minZ, Math.min(z, box.maxZ))
    const dx = x - closestX
    const dz = z - closestZ
    const dxz = Math.sqrt(dx * dx + dz * dz)
    const centerInsideXZ = closestX === x && closestZ === z

    // No XZ overlap when circle center is outside AABB and farther than radius
    if (!centerInsideXZ && dxz >= radius) continue

    // 3D collision: Y and XZ both overlap
    if (centerInsideXZ) {
      // Circle center inside AABB XZ: minimum travel on each XZ face to fully clear it
      const penLeft  = (x - box.minX) + radius
      const penRight = (box.maxX - x) + radius
      const penFront = (z - box.minZ) + radius
      const penBack  = (box.maxZ - z) + radius
      const minXZPen = Math.min(penLeft, penRight, penFront, penBack)

      if (overlapY <= minXZPen) {
        const playerCenterY = y + height / 2
        const boxCenterY    = (box.minY + box.maxY) / 2
        if (playerCenterY > boxCenterY) { y += overlapY; grounded = true }
        else                            { y -= overlapY }
      } else {
        if      (penLeft <= penRight && penLeft <= penFront && penLeft <= penBack)  x = box.minX - radius
        else if (penRight <= penFront && penRight <= penBack)                        x = box.maxX + radius
        else if (penFront <= penBack)                                                z = box.minZ - radius
        else                                                                         z = box.maxZ + radius
      }
    } else {
      // Circle center outside AABB XZ: push along normal from closest point to center
      const xzPen = radius - dxz
      const nx = dx / dxz
      const nz = dz / dxz

      if (overlapY <= xzPen) {
        const playerCenterY = y + height / 2
        const boxCenterY    = (box.minY + box.maxY) / 2
        if (playerCenterY > boxCenterY) { y += overlapY; grounded = true }
        else                            { y -= overlapY }
      } else {
        x += nx * xzPen
        z += nz * xzPen
      }
    }
  }

  return { x, y, z, grounded }
}

// ── Movement-based stair-aware resolver (codex gpt-5.6-sol) ───────────────────
// The min-penetration resolveCollisions above has no travel-direction knowledge,
// so it can't tell an upcoming riser from the higher tread BEHIND a descending
// player — and with radius 0.3 > tread 0.29 a full-radius ground test always
// overlaps the tread behind, making descent impossible. This resolver moves from
// a known-good start pose along a delta, resolving horizontal / step / vertical
// separately, using a direction-biased CENTER-FOOT probe to pick the tread ahead.

export interface Vec3 { x: number; y: number; z: number }
export interface CollisionResult extends Vec3 {
  grounded: boolean
  hitCeiling: boolean
  steppedUp: boolean
  steppedDown: boolean
}
export interface MoveOptions { wasGrounded: boolean; maxStepUp?: number; maxStepDown?: number }

/** Max riser the player climbs: above our 0.1875 m stair riser + kerbs, below the 1.1 m balustrades. */
export const MAX_STEP = 0.24

const EPS = 1e-6
const STEP_EPS = 1e-5
const SUBSTEP_RADIUS_FRACTION = 0.45
const MAX_HORIZONTAL_ITERS = 12

interface HorizontalResult { x: number; z: number }
interface Support { top: number; box: AABB }

function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(v, hi)) }

function verticalIntervalsOverlap(footY: number, height: number, box: AABB): boolean {
  return box.minY < footY + height - EPS && box.maxY > footY + EPS
}

function circleOverlapsBoxXZ(x: number, z: number, radius: number, box: AABB): boolean {
  const qx = clamp(x, box.minX, box.maxX)
  const qz = clamp(z, box.minZ, box.maxZ)
  const dx = x - qx, dz = z - qz
  return dx * dx + dz * dz < radius * radius - EPS
}

function circleBoxCorrection(x: number, z: number, radius: number, box: AABB): { x: number; z: number; depth: number } | null {
  const qx = clamp(x, box.minX, box.maxX)
  const qz = clamp(z, box.minZ, box.maxZ)
  const dx = x - qx, dz = z - qz
  const distSq = dx * dx + dz * dz
  if (distSq > EPS * EPS) {
    if (distSq >= radius * radius) return null
    const dist = Math.sqrt(distSq)
    const depth = radius - dist
    return { x: (dx / dist) * depth, z: (dz / dist) * depth, depth }
  }
  const candidates = [
    { x: box.minX - radius - x, z: 0 },
    { x: box.maxX + radius - x, z: 0 },
    { x: 0, z: box.minZ - radius - z },
    { x: 0, z: box.maxZ + radius - z },
  ]
  let best = candidates[0]
  let bestDepth = Math.abs(best.x) + Math.abs(best.z)
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i]
    const depth = Math.abs(c.x) + Math.abs(c.z)
    if (depth < bestDepth) { best = c; bestDepth = depth }
  }
  return { ...best, depth: bestDepth }
}

function isWalkableLedge(box: AABB, footY: number, maxStepUp: number): boolean {
  const rise = box.maxY - footY
  return rise > STEP_EPS && rise <= maxStepUp + STEP_EPS
}

function resolveHorizontalAtFoot(
  targetX: number, targetZ: number, footY: number, radius: number, height: number,
  colliders: readonly AABB[], maxStepUp: number, allowWalkableLedges: boolean,
): HorizontalResult {
  let x = targetX, z = targetZ
  for (let iteration = 0; iteration < MAX_HORIZONTAL_ITERS; iteration++) {
    let bestCorrection: { x: number; z: number; depth: number } | null = null
    for (const box of colliders) {
      if (!verticalIntervalsOverlap(footY, height, box)) continue
      if (allowWalkableLedges && isWalkableLedge(box, footY, maxStepUp)) continue
      const correction = circleBoxCorrection(x, z, radius, box)
      if (!correction) continue
      if (!bestCorrection || correction.depth > bestCorrection.depth) bestCorrection = correction
    }
    if (!bestCorrection) break
    x += bestCorrection.x
    z += bestCorrection.z
  }
  return { x, z }
}

function pointOnBoxTop(x: number, z: number, box: AABB): boolean {
  return x >= box.minX - EPS && x <= box.maxX + EPS && z >= box.minZ - EPS && z <= box.maxZ + EPS
}

function findSupport(x: number, z: number, minTop: number, maxTop: number, colliders: readonly AABB[]): Support | null {
  let result: Support | null = null
  for (const box of colliders) {
    const top = box.maxY
    if (top < minTop - STEP_EPS) continue
    if (top > maxTop + STEP_EPS) continue
    if (!pointOnBoxTop(x, z, box)) continue
    if (!result || top > result.top) result = { top, box }
  }
  return result
}

function segmentHitsExpandedBoxXZ(x0: number, z0: number, x1: number, z1: number, radius: number, box: AABB): boolean {
  const minX = box.minX - radius, maxX = box.maxX + radius
  const minZ = box.minZ - radius, maxZ = box.maxZ + radius
  const dx = x1 - x0, dz = z1 - z0
  let tMin = 0, tMax = 1
  const testAxis = (p: number, d: number, min: number, max: number): boolean => {
    if (Math.abs(d) <= EPS) return p > min + EPS && p < max - EPS
    let a = (min - p) / d, b = (max - p) / d
    if (a > b) [a, b] = [b, a]
    tMin = Math.max(tMin, a); tMax = Math.min(tMax, b)
    return tMin <= tMax
  }
  return testAxis(x0, dx, minX, maxX) && testAxis(z0, dz, minZ, maxZ)
}

function hasStepHeadClearance(
  x0: number, z0: number, x1: number, z1: number, fromY: number, toY: number,
  radius: number, height: number, colliders: readonly AABB[],
): boolean {
  if (toY <= fromY + STEP_EPS) return true
  const oldHead = fromY + height, newHead = toY + height
  for (const box of colliders) {
    if (box.minY >= newHead - EPS) continue
    if (box.maxY <= oldHead + EPS) continue
    if (segmentHitsExpandedBoxXZ(x0, z0, x1, z1, radius, box)) return false
  }
  return true
}

function projectedProgress(startX: number, startZ: number, result: HorizontalResult, dx: number, dz: number): number {
  const lengthSq = dx * dx + dz * dz
  if (lengthSq <= EPS * EPS) return 0
  return ((result.x - startX) * dx + (result.z - startZ) * dz) / lengthSq
}

interface GroundSubstepResult extends HorizontalResult { y: number; grounded: boolean; steppedUp: boolean; steppedDown: boolean }

function moveGroundSubstep(
  x: number, y: number, z: number, dx: number, dz: number, radius: number, height: number,
  colliders: readonly AABB[], maxStepUp: number, maxStepDown: number,
): GroundSubstepResult {
  const desiredX = x + dx, desiredZ = z + dz
  const free = resolveHorizontalAtFoot(desiredX, desiredZ, y, radius, height, colliders, maxStepUp, true)
  const moveLength = Math.hypot(dx, dz)
  const probeBias = 1e-4
  const probeX = free.x + (moveLength > EPS ? (dx / moveLength) * probeBias : 0)
  const probeZ = free.z + (moveLength > EPS ? (dz / moveLength) * probeBias : 0)
  const support = findSupport(probeX, probeZ, y - maxStepDown, y + maxStepUp, colliders)
  if (!support) return { ...free, y, grounded: false, steppedUp: false, steppedDown: false }
  const rise = support.top - y
  if (rise > STEP_EPS) {
    const blocked = resolveHorizontalAtFoot(desiredX, desiredZ, y, radius, height, colliders, maxStepUp, false)
    const freeProgress = projectedProgress(x, z, free, dx, dz)
    const blockedProgress = projectedProgress(x, z, blocked, dx, dz)
    const walkingIntoLedge = freeProgress > blockedProgress + STEP_EPS
    const headClear = hasStepHeadClearance(x, z, free.x, free.z, y, support.top, radius, height, colliders)
    if (!walkingIntoLedge || !headClear) {
      const oldSupport = findSupport(blocked.x, blocked.z, y - maxStepDown, y + STEP_EPS, colliders)
      return {
        ...blocked,
        y: oldSupport?.top ?? y,
        grounded: oldSupport !== null,
        steppedUp: false,
        steppedDown: oldSupport !== null && oldSupport.top < y - STEP_EPS,
      }
    }
    return { ...free, y: support.top, grounded: true, steppedUp: true, steppedDown: false }
  }
  return { ...free, y: support.top, grounded: true, steppedUp: false, steppedDown: rise < -STEP_EPS }
}

interface VerticalResult { y: number; grounded: boolean; hitCeiling: boolean }

function resolveVertical(
  x: number, y: number, z: number, dy: number, radius: number, height: number,
  colliders: readonly AABB[], probeX: number, probeZ: number,
): VerticalResult {
  if (dy > EPS) {
    const startHead = y + height, endHead = startHead + dy
    let ceilingY: number | null = null
    for (const box of colliders) {
      if (!circleOverlapsBoxXZ(x, z, radius, box)) continue
      const bottom = box.minY
      if (bottom < startHead - EPS) continue
      if (bottom > endHead + EPS) continue
      if (ceilingY === null || bottom < ceilingY) ceilingY = bottom
    }
    if (ceilingY !== null) return { y: ceilingY - height, grounded: false, hitCeiling: true }
    return { y: y + dy, grounded: false, hitCeiling: false }
  }
  if (dy < -EPS) {
    const endFoot = y + dy
    const support = findSupport(probeX, probeZ, endFoot, y, colliders)
    if (support) return { y: support.top, grounded: true, hitCeiling: false }
    return { y: endFoot, grounded: false, hitCeiling: false }
  }
  return { y, grounded: false, hitCeiling: false }
}

/**
 * Move a character from a known valid previous pose `start` along `delta`.
 * Do NOT pass an already-integrated position: travel direction + previous foot
 * height are required to distinguish ascending from descending.
 */
export function moveAndResolveCollisions(
  start: Vec3, delta: Vec3, radius: number, height: number,
  colliders: readonly AABB[], options: MoveOptions,
): CollisionResult {
  const maxStepUp = options.maxStepUp ?? MAX_STEP
  const maxStepDown = options.maxStepDown ?? MAX_STEP
  let x = start.x, y = start.y, z = start.z
  let steppedUp = false, steppedDown = false
  const horizontalLength = Math.hypot(delta.x, delta.z)
  const maxSubstep = Math.max(0.05, radius * SUBSTEP_RADIUS_FRACTION)
  const substeps = Math.max(1, Math.ceil(horizontalLength / maxSubstep))
  const dx = delta.x / substeps, dz = delta.z / substeps
  let followingGround = options.wasGrounded && delta.y <= STEP_EPS
  for (let i = 0; i < substeps; i++) {
    if (followingGround) {
      const result = moveGroundSubstep(x, y, z, dx, dz, radius, height, colliders, maxStepUp, maxStepDown)
      x = result.x; y = result.y; z = result.z
      followingGround = result.grounded
      steppedUp ||= result.steppedUp
      steppedDown ||= result.steppedDown
    } else {
      const result = resolveHorizontalAtFoot(x + dx, z + dz, y, radius, height, colliders, maxStepUp, false)
      x = result.x; z = result.z
    }
  }
  if (followingGround) {
    return { x, y, z, grounded: true, hitCeiling: false, steppedUp, steppedDown }
  }
  const actualDx = x - start.x, actualDz = z - start.z
  const actualHorizontalLength = Math.hypot(actualDx, actualDz)
  const probeBias = 1e-4
  const probeX = x + (actualHorizontalLength > EPS ? (actualDx / actualHorizontalLength) * probeBias : 0)
  const probeZ = z + (actualHorizontalLength > EPS ? (actualDz / actualHorizontalLength) * probeBias : 0)
  const vertical = resolveVertical(x, y, z, delta.y, radius, height, colliders, probeX, probeZ)
  return { x, y: vertical.y, z, grounded: vertical.grounded, hitCeiling: vertical.hitCeiling, steppedUp, steppedDown }
}
